import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import React, { useCallback } from 'react'
import { Dropdown } from 'react-bootstrap';
import { privateApi } from '../../../api/api';
import { toast } from 'sonner';
import Swal from 'sweetalert2';
import LinkRates from './LinkRates';
import RequireAuth from '../../../auth/middleware/RequireAuth';
import ReadOnlyGuard from '../../../shared/components/ReadOnlyGuard';

interface RatesProps {
    room_id: string;
    onCreateLinkRate: (rate_id: string) => void;
    onUpdateRate: (rate_id: string) => void;
    onUpdateLinkRate: (rate_id: string) => void;
}

const Rates: React.FC<RatesProps> = ({ room_id, onCreateLinkRate, onUpdateRate, onUpdateLinkRate }) => {
    const rates = useQuery({
        queryKey: ['rates', room_id],
        queryFn: () => privateApi.get(`/v4/rate-plan`, {
            params: {
                filter: {
                    room_type_id: room_id,
                }
            }
        }).then(res => {
            const result = res.data.data.filter((rate: any) => rate.type === 'rate');
            const linkRates = res.data.data.filter((rate: any) => rate.type === 'link');
            const data = result.map((rate: any) => {
                const children = linkRates.filter((linkRate: any) => linkRate.parent_id === rate.id);
                return {
                    ...rate,
                    children,
                }
            });
            return data;
        }),
    });

    const queryClient = useQueryClient();

    const { mutate } = useMutation({
        mutationFn: (rate_id: string) => privateApi.delete(`/v4/rate-plan/${rate_id}`),
        onSuccess: async () => {
            await queryClient.invalidateQueries({
                queryKey: ['rates', room_id]
            });
            await queryClient.invalidateQueries({
                queryKey: ['rooms', 'rates']
            });

            toast.success('Rate deleted successfully', {
                id: 'delete-rate'
            });

            Swal.fire({
                title: 'Reminder',
                html: 'Rate plan deleted. <strong>Make sure to re-map it in all channels</strong> so the OTA mapping doesn\'t get left behind.',
                icon: 'info',
                confirmButtonText: 'Got it',
            });
        },
        onError: () => {
            toast.error('Failed to delete rate', {
                id: 'delete-rate'
            });
        },
    });

    const handleDelete = useCallback((rate_id: string) => {
        Swal.fire({
            title: 'Are you sure?',
            html: 'You will not be able to recover this rate plan!<br /><br /><strong>Check the Channex mapping</strong> for this rate plan before deleting — it may still be linked to OTA channels.',
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: 'Yes, delete it!',
            confirmButtonColor: '#d33',
            cancelButtonColor: '#3085d6',
            cancelButtonText: 'Cancel',
        }).then((result) => {
            if (result.isConfirmed) {
                toast.loading('Deleting rate...', {
                    id: 'delete-rate'
                });

                mutate(rate_id);
            }
        });
    }, [mutate]);

    return (
        <>
            {!rates.data && rates.isFetching && (
                <tr>
                    <td colSpan={5} className='text-center'>
                        Loading...
                    </td>
                </tr>
            )}

            {rates.isError && (
                <tr>
                    <td colSpan={5} className="text-danger text-center">
                        Failed to fetch rates
                    </td>
                </tr>
            )}

            {rates.isSuccess && rates.data && rates.data.length === 0 && (
                <tr>
                    <td colSpan={5} className="text-center">
                        No rates found
                    </td>
                </tr>
            )}

            {rates.isSuccess && rates.data && (
                rates.data.map((rate: any) => (
                    <React.Fragment key={rate.id}>
                        <tr className="child-rate">
                            <td></td>
                            <td>{rate.name} (Max Guests: {rate.guest_max})</td>
                            <td>—</td>
                            <td>—</td>
                            <td className="text-end">
                                <Dropdown>
                                    <Dropdown.Toggle variant="default" id="action-dropdown">
                                        Actions
                                    </Dropdown.Toggle>
                                    <Dropdown.Menu popperConfig={{ strategy: 'fixed' }}>
                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_update']} asComponent>
                                            <ReadOnlyGuard mode="hide">
                                                <Dropdown.Item onClick={() => onUpdateRate(rate.id)}>Edit</Dropdown.Item>
                                            </ReadOnlyGuard>
                                        </RequireAuth>
                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_create']} asComponent>
                                            <ReadOnlyGuard mode="hide">
                                                <Dropdown.Item onClick={() => onCreateLinkRate(rate.id)}>Add Link Rate</Dropdown.Item>
                                            </ReadOnlyGuard>
                                        </RequireAuth>
                                        {/* Remove stays enabled while the property is inactive — deliberate carve-out, see IndexAi/property-management.md */}
                                        <RequireAuth requiredRoles={['dev']} requiredPermissions={['room_rate_delete']} asComponent>
                                            <Dropdown.Item className="text-danger" onClick={() => handleDelete(rate.id)}>Remove</Dropdown.Item>
                                        </RequireAuth>
                                    </Dropdown.Menu>
                                </Dropdown>
                            </td>
                        </tr>
                        {rate.children.length > 0 && (
                            <LinkRates rate={rate.children} onUpdateLinkRate={onUpdateLinkRate} />
                        )}
                    </React.Fragment>
                ))
            )}
        </>
    )
}

export default Rates